import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import ChatResponse from './ChatResponse';
import './Chatbot.css';
import { getChatbotApiUrl, getChatbotApiKey } from '../utils/config';
import { useTooltip } from '../contexts/TooltipContext';

interface SuggestedPrompt {
  text: string;   // Button text (e.g., "Show me jobs")
  query: string;  // Actual query to send (e.g., "jobs")
  icon: string;   // Emoji icon for the button
}

interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  sources?: any[] | null;
  suggested_prompts?: SuggestedPrompt[] | null;
}

interface ChatResponse {
  answer: string;
  sources?: any[] | null;
  tokens_used?: number;
  last_occupation_title?: string;
  last_occupation_soc?: string;
  suggested_prompts?: SuggestedPrompt[] | null;
}

const Chatbot: React.FC = () => {
  // Tooltip context for dynamic z-index
  const { isTooltipOpen } = useTooltip();
  
  // Session management - create or retrieve session ID
  const [sessionId] = useState(() => {
    let storedSessionId = localStorage.getItem('chat_session_id');
    if (!storedSessionId) {
      storedSessionId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('chat_session_id', storedSessionId);
    }
    return storedSessionId;
  });

  // State management
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      text: "Hi! I'm Alex, your career advisor. I can help you with detailed questions about:\n\n#### **Career Search & Information**\n- Find specific jobs (e.g., 'software developers in healthcare')\n- Career pathways and requirements\n- Salary ranges and job outlook\n\n#### **Education & Training**\n- Degree programs needed\n- Certifications required\n- On-the-job training details\n\n#### **Industry Insights**\n- Job growth trends\n- Skills in demand\n- Career advancement opportunities\n\nYou can ask detailed questions - I can handle up to 500 characters! What would you like to know?",
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastOccupation, setLastOccupation] = useState<string | null>(null);
  const [lastSOC, setLastSOC] = useState<string | null>(null);
  const [showOccupationChange, setShowOccupationChange] = useState(false);
  const [showContextBanner, setShowContextBanner] = useState(true); // New state for banner visibility

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // API configuration - use centralized config utility
  const CHAT_ENDPOINT = getChatbotApiUrl();
  const CHAT_API_KEY = getChatbotApiKey();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check API health on component mount
  useEffect(() => {
    checkApiHealth();
  }, []);

  // Listen for job card clicks to update context
  useEffect(() => {
    const handleJobModalOpened = (event: CustomEvent) => {
      const { job } = event.detail;
      if (job) {
        setLastOccupation(job.title);
        setLastSOC(job.socCode);
        setShowContextBanner(true); // Show banner when occupation is set
        // Show visual indicator that context changed
        setShowOccupationChange(true);
        setTimeout(() => setShowOccupationChange(false), 5000);
      }
    };

    window.addEventListener('jobModalOpened', handleJobModalOpened as EventListener);
    return () => {
      window.removeEventListener('jobModalOpened', handleJobModalOpened as EventListener);
    };
  }, []);

  // Utility functions
  const scrollToBottom = () => {
    // Use requestAnimationFrame to prevent forced reflow
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  };

  const checkApiHealth = async () => {
    try {
      const healthUrl = CHAT_ENDPOINT.replace('/api/chat', '/health');
      const response = await fetch(healthUrl, { 
        signal: AbortSignal.timeout(3000) // 3 second timeout
      });
      // Silently check health - no console logs
    } catch (error) {
      // Silently fail - chatbot backend is optional
    }
  };

  // Event handlers
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    
    // Check character limit (500 characters max for better responses)
    if (userMessage.length > 500) {
      alert('Please keep your message under 500 characters for the best experience.');
      return;
    }

    // Add user message
    const newUserMessage: ChatMessage = {
      id: Date.now().toString(),
      text: userMessage,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newUserMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Build conversation history (last 5 messages for context)
      const conversationHistory = messages
        .slice(-5)
        .map(msg => ({
          role: msg.isUser ? 'user' : 'assistant',
          content: msg.text
        }));

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (CHAT_API_KEY) {
        headers['X-API-KEY'] = CHAT_API_KEY;
      }

      const response = await fetch(CHAT_ENDPOINT, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query: userMessage,
          session_id: sessionId,
          conversation_history: conversationHistory,
          last_occupation: lastOccupation,
          last_soc: lastSOC
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ChatResponse = await response.json();
      
      // Track occupation context from response
      let occupationChanged = false;
      if (data.last_occupation_title && data.last_occupation_soc) {
        const prevOccupation = lastOccupation;
        const prevSOC = lastSOC;
        
        // Always update to what the backend actually used
        setLastOccupation(data.last_occupation_title);
        setLastSOC(data.last_occupation_soc);
        setShowContextBanner(true); // Show banner when occupation context is updated
        
        // Show guidance message if occupation context changed
        // (either title or SOC code changed)
        if (prevOccupation !== data.last_occupation_title || prevSOC !== data.last_occupation_soc) {
          occupationChanged = true;
          setShowOccupationChange(true);
          setTimeout(() => setShowOccupationChange(false), 5000); // Hide after 5 seconds
        }
      }
      
      let responseText = data.answer;
      
      // Add AI response with suggestions
      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: responseText,
        isUser: false,
        timestamp: new Date(),
        sources: data.sources || null,
        suggested_prompts: data.suggested_prompts || null
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      // Add error message
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: "Sorry, I'm having trouble connecting right now. Please try again in a moment.",
        isUser: false,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
    // Allow Shift+Enter for new lines in textarea
  };

  // Simplified chat control functions
  const toggleChat = () => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
      setIsMinimized(false);
      // Focus input after opening
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const minimizeChat = () => {
    setIsOpen(false);
    setIsMinimized(true);
  };

  const clearContext = () => {
    setLastOccupation(null);
    setLastSOC(null);
    setShowContextBanner(false);
  };


  // Render minimized state
  if (isMinimized) {
    return (
      <div className={`chatbot-container ${isTooltipOpen ? 'chatbot-low-zindex' : ''}`}>
        <button
          onClick={toggleChat}
          className="chatbot-toggle-button chatbot-minimized"
          title="Open Chat"
          aria-label="Open chat"
        >
          <svg className="chatbot-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9h8M8 13h6" />
          </svg>
        </button>
      </div>
    );
  }

  // Render main component
  return (
    <div className={`chatbot-container ${isTooltipOpen ? 'chatbot-low-zindex' : ''}`}>
      
      
      {/* Chat Toggle Button - Only show when chat is closed */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="chatbot-toggle-button"
          title="Chat with Career Advisor"
          aria-label="Open chat with career advisor"
        >
          <svg className="chatbot-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9h8M8 13h6" />
          </svg>
        </button>
      )}

      {/* Chat Window - Only show when chat is open */}
      {isOpen && (
        <div className="chatbot-window">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-content">
              <div className="chatbot-avatar">
                <span className="chatbot-avatar-text">A</span>
              </div>
              <div className="chatbot-header-text">
                <h3 className="chatbot-title">Career Advisor</h3>
                <p className="chatbot-subtitle">Ask me anything about careers!</p>
              </div>
            </div>
            <div className="chatbot-header-actions">
              <button
                onClick={minimizeChat}
                className="chatbot-header-button"
                title="Minimize"
                aria-label="Minimize chat"
              >
                <svg className="chatbot-header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <button
                onClick={toggleChat}
                className="chatbot-header-button"
                title="Close"
                aria-label="Close chat"
              >
                <svg className="chatbot-header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Context Bar - Show current occupation context */}
          {(lastOccupation || lastSOC) && showContextBanner && (
            <div style={{
              padding: '6px 8px',
              background: showOccupationChange 
                ? 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)' 
                : 'linear-gradient(135deg, #f2e8d5 0%, #f9f1e3 100%)',
              borderBottom: showOccupationChange ? '1px solid #28a745' : '1px solid #c1a27b',
              fontSize: '11px',
              color: showOccupationChange ? '#155724' : '#5a483b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between', /* Changed to space-between for dismiss button */
              gap: '4px',
              transition: 'all 0.3s ease',
              flexWrap: 'wrap',
              wordBreak: 'break-word',
              overflowWrap: 'break-word'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }}>
                <svg style={{ width: '12px', height: '12px', color: showOccupationChange ? '#28a745' : '#d5a32a', flexShrink: 0 }} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                  <strong>{showOccupationChange ? '💡 Now discussing:' : 'Talking about:'}</strong>
                  <span>{lastOccupation}</span>
                  {lastSOC && <span style={{ fontSize: '10px', opacity: 0.8 }}>(SOC: {lastSOC})</span>}
                </span>
              </div>
              <button
                onClick={clearContext}
                style={{
                  background: 'none',
                  border: 'none',
                  color: showOccupationChange ? '#155724' : '#5a483b',
                  cursor: 'pointer',
                  padding: '2px',
                  borderRadius: '3px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '20px',
                  minHeight: '20px',
                  opacity: 0.7,
                  transition: 'opacity 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                title="Clear context"
                aria-label="Clear occupation context"
              >
                <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
          )}

          {/* Messages Area */}
          <div className="chatbot-messages">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`chatbot-message ${message.isUser ? 'chatbot-message-user' : 'chatbot-message-ai'}`}
              >
                <div className={`chatbot-message-bubble ${message.isUser ? 'chatbot-message-bubble-user' : 'chatbot-message-bubble-ai'}`}>
                  <div className="chatbot-message-text">
                    {message.isUser ? (
                      <ReactMarkdown>{message.text}</ReactMarkdown>
                    ) : (
                      <ChatResponse 
                        response={message.text} 
                        suggestedPrompts={message.suggested_prompts}
                        onSelectSuggestion={(query) => {
                          setInputValue(query);
                          setTimeout(() => handleSendMessage(), 100);
                        }}
                      />
                    )}
                  </div>
                  <p className={`chatbot-message-time ${message.isUser ? 'chatbot-message-time-user' : 'chatbot-message-time-ai'}`}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            
            {/* Loading Indicator */}
            {isLoading && (
              <div className="chatbot-message chatbot-message-ai">
                <div className="chatbot-message-bubble chatbot-message-bubble-ai">
                  <div className="chatbot-loading">
                    <div className="chatbot-loading-dot"></div>
                    <div className="chatbot-loading-dot"></div>
                    <div className="chatbot-loading-dot"></div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="chatbot-input-container">
            <div className="chatbot-input-wrapper">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Ask detailed questions about careers, education, jobs, salary ranges, requirements..."
                className="chatbot-input chatbot-textarea"
                disabled={isLoading}
                aria-label="Type your message"
                rows={3}
                maxLength={500}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="chatbot-send-button"
                aria-label="Send message"
              >
                <svg className="chatbot-send-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
            <p className="chatbot-input-hint">
              {inputValue.length}/500 characters • Powered by AI Career Advisor
            </p>
          </div>
        </div>
      )}


    </div>
  );
};

export default Chatbot;
